import { IntegrationServices } from '@sequencehq/api/commonEnums'
import { LinkSalesforceCustomerModal } from './integrationsConfig/salesforce/InspectorItem/LinkSalesforceCustomerModal/LinkSalesforceCustomerModal'
import { FC } from 'react'
import { LinkOrCreateCustomerModal } from 'Integrations/integrationsConfig/common/LinkEntities/LinkCustomerToService/LinkOrCreateCustomerModal'
import { INTEGRATIONS_CONFIG } from './integrationsConfig/integrations.config'
import { Integration } from './domain'
export { IntegrationsRoot } from './view/integrations.root'
export { useIntegrationsDomainContext } from './communication'
export { useReconnectToIntegration } from './utils/useReconnectToIntegration'
export { useIntegrationActive } from './utils/useIntegrationActive'
/**
 * All of these components may be used anywhere in the wider application,
 * but have a dependency on the Root having been mounted, to provide the
 * appropriate context. Integrations is a globally impactful feature, so we
 * expect to add more exportable components to this module over time.
 *
 * We should, when the pattern falls into place, make sure these components are
 * more flexible by allowing for adapter to be swapped out rather than a 'hard'
 * dependency on the above module root. But for now they'll be 'drop in and go' regardless.
 */
export { IntegrationsNavigation } from './view/integrationsNavigation/IntegrationsNavigation'
export { IntegrationManagement } from './view/integrationManagement/IntegrationManagement'
export { IntegrationsBrowserModal } from './view/integrationsBrowserModal/IntegrationsBrowserModal'

/**
 * These components are common 'inspector widgets' associated to the integrations that
 * may be used in various contexts.
 */
export * from './integrationsConfig/stripe/InspectorItem'
export * from './integrationsConfig/goCardless/InspectorItem'
export * from './integrationsConfig/xero/InspectorItem'
export * from './integrationsConfig/hubSpot/InspectorItem'
export * from './integrationsConfig/quickBooks/InspectorItem'
export * from './integrationsConfig/salesforce/InspectorItem'
export * from './integrationsConfig/rillet/InspectorItem'
export * from './integrationsConfig/netsuite/InspectorItem'
export { NetsuiteItemSelect } from './integrationsConfig/netsuite/NetsuiteItemSelect/NetsuiteItemSelect'
export {
  NETSUITE_ITEM_ACCOUNT_DEFAULT_LABEL,
  NETSUITE_ITEM_STALE_TIME,
  findNetsuiteItemId,
  netsuiteItemFallbackLabel
} from './integrationsConfig/netsuite/NetsuiteItemSelect/netsuiteItem.constants'

/**
 * Integration-specific blocking dialogs shown by other modules when an action
 * can't proceed until the integration is configured.
 */
export * from './integrationsConfig/avalara'

/**
 * These components are the Link/Unlink modals for the relevant integrations. Most of the
 * time, these would be accessed via the inspector item, but we also use them in the
 * customer list view.
 */
export { LinkCustomerModal } from './integrationsConfig/common/LinkEntities/LinkCustomerToService/LinkCustomerModal'
export { UnlinkCustomerModal } from './integrationsConfig/common/LinkEntities/LinkCustomerToService/UnlinkCustomerModal'
export { ResolveCustomerLinkButton } from './integrationsConfig/common/LinkEntities/LinkCustomerToService/ResolveCustomerLinkButton'

/**
 * For using icons
 */
export const INTEGRATION_DETAILS: Partial<
  Record<
    Integration['id'],
    {
      logoUrl: string
      smallLogoUrl: string
      label: string
    }
  >
> = Object.entries(INTEGRATIONS_CONFIG).reduce((acc, [key, value]) => {
  return {
    ...acc,
    [key]: {
      logoUrl: value.logoUrl,
      smallLogoUrl: value.smallLogoUrl,
      label: value.title
    }
  }
}, {})

export {
  IntegrationCustomerPicker,
  IntegrationCustomerSelect,
  getValidationFieldLabels,
  formatListWithOverflow,
  matchesFilters
} from './components/integrationCustomerPicker'
export type {
  CountryFilterControl,
  CustomerValidationError,
  CustomerValidationErrorProblem,
  SelectAllControl,
  SelectableCustomer,
  SelectableCustomerContact
} from './components/integrationCustomerPicker'

export { SmallIcon } from './integrationsConfig/common/SmallIcon'
export { TableLinksLogos } from './integrationsConfig/common/TableLinksLogos'

export const customCustomerLinkModals: Partial<
  Record<
    IntegrationServices,
    FC<{
      customerId: string
      onLink?: () => void
      onClose: () => void
    }>
  >
> = {
  Salesforce: LinkSalesforceCustomerModal,
  Xero: props => <LinkOrCreateCustomerModal service="Xero" {...props} />,
  QuickBooks_Online: props => (
    <LinkOrCreateCustomerModal service="QuickBooks_Online" {...props} />
  ),
  NetSuite: props => <LinkOrCreateCustomerModal service="NetSuite" {...props} />
}

export { EXCLUSIVE_PAYMENT_PROVIDER_MESSAGE } from './constants'
export {
  GOCARDLESS_UNSUPPORTED_CURRENCY_MESSAGE,
  GOCARDLESS_UNSUPPORTED_COUNTRY_MESSAGE,
  GOCARDLESS_CANNOT_REMOVE_LAST_PRODUCT_MESSAGE
} from './integrationsConfig/goCardless/constants'
export { getGoCardlessDisabledReason } from './utils/getGoCardlessDisabledReason'

export { useStripeIntegration } from './hooks/useStripeIntegration'
export { useGoCardlessIntegration } from './hooks/useGoCardlessIntegration'
export { useLoadLedgerIntegrations } from './hooks/useLoadLedgerIntegrations'
export { StripeIcon } from './integrationsConfig/stripe/InspectorItem'
export { pipelineStageDisplayLabels } from './integrationsConfig/hubSpot/pipelineStageLabels'
export { GoCardlessIcon } from './integrationsConfig/goCardless/InspectorItem'
